<ph-stack>
    <ph-columns *ngIf="compact" min="280px">
        <ng-container *ngTemplateOutlet="googleCard"></ng-container>
        <ng-container *ngTemplateOutlet="smsCard"></ng-container>
    </ph-columns>

    <ng-container *ngIf="!compact">
        <ng-container *ngTemplateOutlet="googleCard"></ng-container>
        <ng-container *ngTemplateOutlet="smsCard"></ng-container>

        <ph-card *ngIf="isLoading" heading="Recovery codes">
            <ph-skeleton lines="2" label="Loading recovery codes"></ph-skeleton>
        </ph-card>

        <ph-card *ngIf="!isLoading && (isGoogleAuthEnabled || isSmsAuthEnabled)"
                 heading="Recovery codes"
                 subheading="Sign in with a recovery code if you lose access to your authenticator app or phone."
                 subheading-wrap>
            <ph-status-badge slot="actions" variant="light" [status]="hasRecoveryCodes ? 'completed' : 'disabled'">{{ hasRecoveryCodes ? 'Generated' : 'None yet' }}</ph-status-badge>

            <div *ngIf="!hasRecoveryCodes">
                You do not have any yet. Generate a set and save them somewhere safe, as they are shown only once.
            </div>
            <div *ngIf="hasRecoveryCodes">
                Regenerating replaces your existing codes. Your previous codes will stop working, and the new ones are shown only once.
            </div>

            <ph-actions slot="footer">
                <ph-button variant="secondary"
                           mode="filled"
                           (click)="regenerateRecoveryCodes()"
                           [disabled]="isRegenerating">
                    {{ recoveryCodesButtonLabel }}
                </ph-button>
            </ph-actions>
        </ph-card>
    </ng-container>
</ph-stack>

<ng-template #googleCard>
    <ph-card heading="Google Authenticator"
             [subheading]="compact ? '' : 'Two-factor authentication with a code from an authenticator app on your phone.'"
             subheading-wrap
             [tone]="highlight2fa && !isGoogleAuthEnabled ? 'warning' : 'none'">
        <ph-badge *ngIf="highlight2fa && !isGoogleAuthEnabled" slot="actions" variant="light" color="orange">Recommended</ph-badge>
        <ph-status-badge *ngIf="!isLoading && !(highlight2fa && !isGoogleAuthEnabled)"
                         slot="actions"
                         variant="light"
                         [status]="isGoogleAuthEnabled ? 'completed' : 'disabled'">{{ isGoogleAuthEnabled ? 'Enabled' : 'Not set up' }}</ph-status-badge>

        <ph-skeleton *ngIf="isLoading" lines="2" label="Loading Google Authenticator"></ph-skeleton>

        <ng-container *ngIf="!isLoading">
            <div *ngIf="isGoogleAuthEnabled">
                Enter the code displayed by your authenticator app when signing in.
            </div>
            <div *ngIf="!isGoogleAuthEnabled">
                An authenticator app lets you generate security codes on your phone without needing to receive text messages.
                If you don't already have one, we recommend Google Authenticator, which you can download
                <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="nofollow noopener">here (iOS)</a> or
                <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US&gl=US" target="_blank" rel="nofollow noopener">here (Android)</a>.
            </div>
        </ng-container>

        <ph-actions slot="footer" *ngIf="!isLoading">
            <ph-button *ngIf="isGoogleAuthEnabled" variant="secondary" mode="filled" (click)="disableGoogleAuth()">Disable Google Authenticator</ph-button>
            <ph-button *ngIf="!isGoogleAuthEnabled" (click)="setupGoogleAuth()">Set up Google Authenticator</ph-button>
        </ph-actions>
    </ph-card>
</ng-template>

<ng-template #smsCard>
    <ph-card heading="Text message (SMS)"
             [subheading]="compact ? '' : 'Two-factor authentication with a code sent to your phone by text message.'"
             subheading-wrap>
        <ph-status-badge *ngIf="!isLoading"
                         slot="actions"
                         variant="light"
                         [status]="isSmsAuthEnabled ? 'completed' : 'disabled'">{{ isSmsAuthEnabled ? 'Enabled' : 'Not set up' }}</ph-status-badge>

        <ph-skeleton *ngIf="isLoading" lines="2" label="Loading text message authentication"></ph-skeleton>

        <ng-container *ngIf="!isLoading">
            <div *ngIf="isSmsAuthEnabled">
                Text verification codes will be sent to the phone number ending in {{ smsLast4 }}.
            </div>
            <div *ngIf="!isSmsAuthEnabled">
                We'll send a code via text message to a phone number you specify on each login attempt.
            </div>
        </ng-container>

        <ph-actions slot="footer" *ngIf="!isLoading">
            <ph-button *ngIf="isSmsAuthEnabled" variant="secondary" mode="filled" (click)="disableSmsAuth()">Disable SMS authentication</ph-button>
            <ph-button *ngIf="!isSmsAuthEnabled" (click)="setupSmsAuth()">Set up SMS authentication</ph-button>
        </ph-actions>
    </ph-card>
</ng-template>

<app-setup-google-authenticator [open]="isSetupGoogleOpen" (closed)="onSetupGoogleClosed($event)"></app-setup-google-authenticator>
<app-setup-sms-authentication [open]="isSetupSmsOpen" (closed)="onSetupSmsClosed($event)"></app-setup-sms-authentication>
<app-confirm-2fa-code [open]="twoFactorOpen" [data]="twoFactorData" (closed)="onTwoFactorClosed($event)"></app-confirm-2fa-code>
<app-recovery-codes-dialog [open]="recoveryCodesOpen" [codes]="recoveryCodes" (closed)="onRecoveryCodesClosed()"></app-recovery-codes-dialog>
